import { useEffect, useRef, useReducer } from 'react'
import PropTypes from 'prop-types'
import classnames from 'classnames/bind'
import { isEmpty, findIndex, cloneDeep } from 'lodash-es'

// Libs
import { initialState, reducer as selectReducer, types as selectTypes } from './reducer/selectReducer'

// Components
import MenuList from './components/MenuList'
import MultiValue from './components/MultiValue'
import Input from './components/Input'
import Icon from '@/assets/icons'

// Styles
import styles from './style.module.scss'

const cx = classnames.bind(styles)

export const propTypes = {
  value: PropTypes.array,
  handleOptionChange: PropTypes.func,
  options: PropTypes.array,
  placeholder: PropTypes.string,
  isMulti: PropTypes.bool,
  isClearable: PropTypes.bool,
  isAutosuggest: PropTypes.bool,
  isDisabled: PropTypes.bool,
  isShowDisplay: PropTypes.bool,
  maxLength: PropTypes.number,
  defaultValue: PropTypes.array,
  isLargeMenu: PropTypes.bool,
  fieldItemRef: PropTypes.object,
}

const defaultProps = {
  isAutosuggest: false,
  isClearable: false,
  isMulti: false,
  isDisabled: false,
  isShowDisplay: false,
  handleOptionChange: () => {},
  isLargeMenu: false,
}

function SelectComponent(props) {
  const {
    value,
    handleOptionChange,
    options,
    placeholder,
    isMulti,
    isClearable,
    isAutosuggest,
    isDisabled,
    isShowDisplay,
    maxLength,
    defaultValue,
    isLargeMenu,
    fieldItemRef,
  } = props

  const newInitialState = { ...initialState, selectedOption: value }
  const [state, dispatch] = useReducer(selectReducer, newInitialState)

  const { isMenuOpened, inputValue, currentOptions, selectedOption } = state

  const hasSelectedOption = !isEmpty(value)

  const controlRef = useRef(null)
  const inputRef = useRef(null)
  const menuRef = useRef(null)

  useEffect(() => {
    dispatch({
      type: selectTypes.INITIAL_STATE,
      options,
      selectedOption: !isEmpty(defaultValue) ? defaultValue : [],
    })
  }, [options]) // TODO: defaultValue 放進 dependency 會無限迴圈，但 defaultValue 是固定值，還要確定為什麼會無限迴圈

  useEffect(() => {
    if (!menuRef?.current) return

    const menuRect = menuRef?.current?.getBoundingClientRect()
    const bodyRect = document.body.getBoundingClientRect()

    const BUFFER_HEIGHT = 60

    const isTopMenu = bodyRect?.bottom < menuRect?.bottom + BUFFER_HEIGHT

    const isRightMenu = menuRect.left + menuRect.width > bodyRect.width + 5

    if (isTopMenu) {
      menuRef.current.style.bottom = `100%`
    } else {
      menuRef.current.style.top = '100%'
    }

    if (isRightMenu) {
      menuRef.current.style.right = `0px`
      menuRef.current.style.left = `initial`
    } else {
      menuRef.current.style.right = `initial`
      menuRef.current.style.left = `0px`
    }
  }, [isMenuOpened])

  useEffect(() => {
    // 因為目前 FieldItem 的設計，只好把 onControlClick 做在 fieldItem 的 onClick
    // 理想上 onControlClick 只做在 Select

    const fieldItem = fieldItemRef?.current

    const handleFieldItemClick = (event) => {
      const isMenuListClick = menuRef.current?.contains(event.target)

      if (!isMenuListClick) onControlClick()
    }

    if (fieldItem) {
      fieldItem.addEventListener('click', handleFieldItemClick)
    }

    return () => {
      if (fieldItem) {
        fieldItem.removeEventListener('click', handleFieldItemClick)
      }
    }
  }, [fieldItemRef])

  const handleInputChange = (event) => {
    if (!isAutosuggest || isDisabled) return

    const currentValue = event.target.value

    dispatch({
      type: selectTypes.HANDLE_INPUT_CHANGE,
      value: currentValue,
    })
  }

  const onControlClick = (event) => {
    if (isDisabled) return

    inputRef.current?.focus()

    // 如果是點擊 multi-remove 就不 dispatch HANDLE_CONTROL_CLICK
    const multiRemoveList = document.getElementsByName('multi-remove')

    let isMultiRemoveClick = false

    multiRemoveList.forEach((item) => {
      if (item?.contains(event?.target)) isMultiRemoveClick = true
    })

    if (!isMultiRemoveClick) {
      dispatch({
        type: selectTypes.HANDLE_CONTROL_CLICK,
        isMenuOpened: true,
      })
    }
  }

  const handleAllClear = () => {
    handleOptionChange([])

    dispatch({
      type: selectTypes.HANDLE_ALL_CLICK,
      options,
    })
  }

  const handleMultiValueRemove = ({ label, value, order }) => {
    const selectedIndex = findIndex(state.selectedOption, { label })

    const cloneSelectedOption = cloneDeep(state.selectedOption)

    const newSelectedOption = [
      ...cloneSelectedOption.slice(0, selectedIndex),
      ...cloneSelectedOption.slice(selectedIndex + 1, cloneSelectedOption.length),
    ]

    handleOptionChange(newSelectedOption)

    dispatch({
      type: selectTypes.HANDLE_MULTI_REMOVE,
      selectedOption: { label, value, order },
    })
  }

  return (
    <div className={cx('select')}>
      <div className={cx('select-wrapper')}>
        <div className={cx('select-control')} ref={controlRef} tabIndex={-1} data-is-disabled={isDisabled}>
          <div className={cx('select-control-value')} data-is-multi={isMulti}>
            {hasSelectedOption && !inputValue && !isMulti && (
              <div className={cx('select-control-value-single')}>{isShowDisplay ? value[0]?.display : value[0]?.label}</div>
            )}

            {isMulti &&
              selectedOption.map((item) => (
                <MultiValue key={item.value} dispatch={dispatch} handleMultiValueRemove={handleMultiValueRemove} {...item} />
              ))}

            {isAutosuggest && (
              <Input
                inputRef={inputRef}
                placeholder={hasSelectedOption ? '' : placeholder}
                hasSelectedOption={hasSelectedOption}
                handleInputChange={handleInputChange}
                value={inputValue}
                isMulti={isMulti}
                isAutosuggest={isAutosuggest}
                isDisabled={isDisabled}
              />
            )}
          </div>

          {isClearable && hasSelectedOption && (
            <div className={cx('select-control-indicator')} onClick={handleAllClear}>
              <Icon.Close className={cx('select-control-indicator-clear')} />
            </div>
          )}

          <div className={cx('select-control-dropdown')}>
            <Icon.Dropdown className={cx('select-control-dropdown-icon')} data-is-open={isMenuOpened} />
          </div>
        </div>

        {isMenuOpened && (
          <div className={cx('select-menu', { '--large': isLargeMenu })} ref={menuRef}>
            <MenuList
              options={currentOptions}
              selectedOption={selectedOption}
              controlRef={controlRef}
              inputRef={inputRef}
              fieldItemRef={fieldItemRef}
              dispatch={dispatch}
              handleOptionChange={handleOptionChange}
              isMulti={isMulti}
              maxLength={maxLength}
            />
          </div>
        )}
      </div>
    </div>
  )
}

SelectComponent.propTypes = propTypes
SelectComponent.defaultProps = defaultProps

export default SelectComponent
